import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { getDashboard } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  VideoCamera,
  FilmStrip,
  Clock,
  CheckCircle,
  Warning,
  TrendUp,
  Users,
  CalendarBlank,
} from "@phosphor-icons/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
  <div className="stat-card">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-400 mb-1">{label}</p>
        <p className="text-3xl font-heading font-semibold text-white">{value}</p>
        {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
      </div>
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${color}20` }}
      >
        <Icon size={20} style={{ color }} weight="fill" />
      </div>
    </div>
  </div>
);

const ChannelHealthCard = ({ channel }) => {
  const isWarning = channel.days_since_publish && channel.days_since_publish > 7;
  
  return (
    <Link
      to={`/channels/${channel.id}`}
      className="surface-card p-4 hover:border-white/10 transition-all group"
      data-testid={`channel-health-${channel.id}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: channel.color }}
        />
        <h4 className="text-sm font-medium text-white truncate group-hover:text-blue-400 transition-colors">
          {channel.name}
        </h4>
        {isWarning && (
          <Warning size={16} className="text-amber-400 ml-auto flex-shrink-0" />
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <p className="text-gray-500">In Pipeline</p>
          <p className="text-white font-medium">{channel.video_count}</p>
        </div>
        <div>
          <p className="text-gray-500">Last Published</p>
          <p className={`font-medium ${isWarning ? "text-amber-400" : "text-white"}`}>
            {channel.days_since_publish !== null
              ? `${channel.days_since_publish}d ago`
              : "Never"}
          </p>
        </div>
      </div>
    </Link>
  );
};

const UpcomingVideo = ({ video }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/[0.02] transition-colors">
    <div
      className="w-2 h-2 rounded-full flex-shrink-0"
      style={{ backgroundColor: video.channel_color || "#3B82F6" }}
    />
    <div className="flex-1 min-w-0">
      <p className="text-sm text-white truncate">{video.title}</p>
      <p className="text-xs text-gray-500">{video.channel_name}</p>
    </div>
    <div className="text-right flex-shrink-0">
      <p className="text-xs text-gray-400">{video.scheduled_date}</p>
      <span className={`badge badge-${video.stage}`}>{video.stage}</span>
    </div>
  </div>
);

const xAxisTick = { fill: "#718096", fontSize: 12 };
const yAxisTick = { fill: "#A0AEC0", fontSize: 12 };
const tooltipCursor = { fill: "rgba(255,255,255,0.02)" };

const STAGE_COLORS = {
  idea: "#64748B",
  script: "#D946EF",
  filming: "#F59E0B",
  editing: "#3B82F6",
  review: "#F43F5E",
  ready: "#10B981",
  scheduled: "#06B6D4",
  published: "#22C55E",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getDashboard();
        setData(response.data);
      } catch (err) {
        // Dashboard fetch error silently handled
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="stat-card">
              <div className="skeleton h-6 w-20 mb-2" />
              <div className="skeleton h-8 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Failed to load dashboard</p>
      </div>
    );
  }

  const { stats, pipeline_stats, channel_health, editor_workload, upcoming, overdue } = data;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1A1A2E] border border-white/10 rounded-lg px-3 py-2 shadow-lg">
          <p className="text-white text-sm capitalize">{label}</p>
          <p className="text-blue-400 text-sm">{payload[0].value} videos</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="mb-2">
        <h1 className="font-heading text-2xl font-semibold text-white">
          Welcome back, {user?.name?.split(" ")[0] || "User"}
        </h1>
        <p className="text-gray-400 text-sm">
          Here&apos;s what&apos;s happening with your YouTube empire
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={VideoCamera}
          label="Active Channels"
          value={stats.active_channels}
          color="#3B82F6"
          subtext={`${stats.total_channels} total`}
        />
        <StatCard
          icon={FilmStrip}
          label="Videos in Pipeline"
          value={stats.videos_in_pipeline}
          color="#F59E0B"
          subtext={`${stats.videos_pending} with editors`}
        />
        <StatCard
          icon={CheckCircle}
          label="Ready to Publish"
          value={stats.videos_ready}
          color="#10B981"
        />
        <StatCard
          icon={TrendUp}
          label="Published This Month"
          value={stats.published_this_month}
          color="#22C55E"
          subtext={`${stats.shorts_this_month} shorts`}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Chart */}
        <div className="surface-card p-6">
          <h3 className="font-heading text-lg font-medium text-white mb-4">
            Pipeline Overview
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pipeline_stats} layout="vertical">
                <XAxis type="number" tick={xAxisTick} />
                <YAxis
                  type="category"
                  dataKey="stage"
                  tick={yAxisTick}
                  width={80}
                  tickFormatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
                />
                <Tooltip content={<CustomTooltip />} cursor={tooltipCursor} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {pipeline_stats.map((entry) => (
                    <Cell key={`cell-${entry.stage}`} fill={STAGE_COLORS[entry.stage] || "#3B82F6"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Editor Workload */}
        <div className="surface-card p-6">
          <h3 className="font-heading text-lg font-medium text-white mb-4">
            Editor Workload
          </h3>
          <div className="space-y-4">
            {editor_workload.map((editor) => {
              const percentage = (editor.current / editor.max) * 100;
              const isOverloaded = percentage > 80;
              
              return (
                <div key={editor.id}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-gray-400" />
                      <span className="text-sm text-white">{editor.name}</span>
                    </div>
                    <span className={`text-xs ${isOverloaded ? "text-amber-400" : "text-gray-400"}`}>
                      {editor.current}/{editor.max}
                    </span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        isOverloaded ? "bg-amber-500" : "bg-blue-500"
                      }`}
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
            {editor_workload.length === 0 && (
              <p className="text-gray-500 text-sm text-center py-4">No editors found</p>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Channel Health */}
        <div className="surface-card p-6 lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-medium text-white">
              Channel Health
            </h3>
            <Link to="/channels" className="text-sm text-blue-400 hover:text-blue-300">
              View all
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-3 max-h-80 overflow-y-auto scrollbar-thin">
            {channel_health.slice(0, 6).map((channel) => (
              <ChannelHealthCard key={channel.id} channel={channel} />
            ))}
          </div>
        </div>

        {/* Upcoming */}
        <div className="surface-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CalendarBlank size={18} className="text-blue-400" />
              <h3 className="font-heading text-lg font-medium text-white">
                Upcoming (7 days)
              </h3>
            </div>
            <Link to="/calendar" className="text-sm text-blue-400 hover:text-blue-300">
              Calendar
            </Link>
          </div>
          <div className="space-y-1 max-h-72 overflow-y-auto scrollbar-thin">
            {upcoming.length > 0 ? (
              upcoming.map((video) => <UpcomingVideo key={video.id} video={video} />)
            ) : (
              <p className="text-gray-500 text-sm text-center py-8">
                No videos scheduled for the next 7 days
              </p>
            )}
          </div>
        </div>

        {/* Overdue */}
        <div className="surface-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Warning size={18} className="text-red-400" />
              <h3 className="font-heading text-lg font-medium text-white">
                Overdue & Alerts
              </h3>
            </div>
            <span className="text-xs text-red-400 font-medium">
              {overdue.length} items
            </span>
          </div>
          <div className="space-y-1 max-h-72 overflow-y-auto scrollbar-thin">
            {overdue.length > 0 ? (
              overdue.map((video) => (
                <div
                  key={video.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-red-500/5 border border-red-500/10"
                >
                  <Clock size={16} className="text-red-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{video.title}</p>
                    <p className="text-xs text-red-400">
                      Scheduled: {video.scheduled_date}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <CheckCircle size={32} className="text-green-400 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">All caught up!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
